/**
 * App 环境下的页面埋点工具
 *
 * 问题：App 内每个页面是独立 WebView，不会触发路由切换，自动埋点失效
 * 解决：监听 Native 生命周期回调 + 页面可见性变化，手动埋点
 *
 * 核心逻辑：
 * 1. 页面加载 → 上报页面曝光
 * 2. 页面隐藏（visibilitychange hidden）→ 上报页面停留（切换tab时触发）
 * 3. 页面显示（visibilitychange visible）→ 重置状态
 * 4. App 切换到后台（tcAppOnPause）→ 上报页面停留
 * 5. App 切换到前台（tcAppOnResume）→ 重置状态
 * 6. ios 中 app （tcAppOnAppear）初始化加载也会触发，这时候埋点。
 */

import { isApp, tcAppOnPause, tcAppOnAppear } from '@/utils/bridge'
import { track } from '@/plugins/event-tracker'
import router from '@/router'

/**
 * 3 主 Tab 路径白名单 —— 与 src/views/Home/index.vue NAV_TABS 对齐
 *
 * 单 WebView 改造后，3 主 Tab 互切走 H5 router.push（同一 WebView 内），
 * Native 不会推 tcAppOnAppear/tcAppOnPause，需要 router.afterEach 兜底补埋点。
 *
 * 严格白名单约束：仅当 from / to 都属于主 Tab 时生效，避免与 Native bridge
 * 推送（二级页 push/pop、切前后台、Mine 进入）产生双重上报。
 */
const MAIN_TAB_PATHS = new Set(['/chat', '/bill', '/details'])

/**
 * 页面进入时间（用于计算停留时长）
 */
let pageEnterTime = 0

/**
 * 当前页面 URL
 */
let currentPageUrl = ''

/**
 * 可见性状态（用于计算实际可见时长）
 */
const visibilityState = {
  isVisible: true,
  accumulatedTime: 0, // 累计可见时长
  lastVisibleTime: 0, // 最后一次可见的时间戳
}

/**
 * 初始化 App 环境下的页面埋点
 *
 * 使用方式：在 main.ts 中调用
 * ```typescript
 * if (isApp()) {
 *   setupAppPageTracking()
 * }
 * ```
 */
export function setupAppPageTracking() {
  if (!isApp()) {
    console.warn('[App Page Tracking] 只在 App 环境中使用')
    return
  }

  // 初始化状态
  pageEnterTime = Date.now()
  currentPageUrl = window.location.href
  visibilityState.isVisible = true
  visibilityState.lastVisibleTime = Date.now()

  // 标记：初始 PV 是否已上报（防止 tcAppOnAppear 和兜底逻辑重复触发）
  let initialViewTracked = false

  // ===== 1. 兜底机制：如果 tcAppOnAppear 超时未触发，自行上报初始 PV =====
  // 正常情况下由 tcAppOnAppear 统一触发，此处仅作为安全兜底
  const fallbackTimer = setTimeout(() => {
    if (!initialViewTracked && document.visibilityState !== 'hidden') {
      initialViewTracked = true
      console.log('[App Page Tracking] tcAppOnAppear 超时未触发，兜底上报初始 PV')
      if (router.currentRoute.value.name) {
        trackPageView()
      } else {
        setTimeout(() => trackPageView(), 100)
      }
    }
  }, 2000)

  // ===== 2. 监听 Native 生命周期（主要上报时机） =====

  // App 切换到后台 → 上报页面停留
  tcAppOnPause(() => {
    console.log('[App Page Tracking] App 切换到后台，开始上报页面停留')
    trackPageStay()
  })

  // 页面初始化 & 从后台恢复时由 tcAppOnAppear 统一触发 PV
  tcAppOnAppear(() => {
    // 首次触发时，取消兜底计时器
    if (!initialViewTracked) {
      initialViewTracked = true
      clearTimeout(fallbackTimer)
    }

    console.log('[App Page Tracking] tcAppOnAppear 触发')
    // 重置状态（重新开始计算停留时长）
    pageEnterTime = Date.now()
    visibilityState.accumulatedTime = 0
    visibilityState.isVisible = true
    visibilityState.lastVisibleTime = Date.now()

    // 上报页面曝光
    trackPageView()
  })

  // ===== 3. 3 主 Tab 互切补埋点（同 WebView 内 router.push 切换，Native 感知不到） =====
  // 严格白名单：仅 from/to 都在主 Tab 集合时触发，避免与 Native bridge 路径冲突
  router.afterEach((to, from) => {
    if (!MAIN_TAB_PATHS.has(from.path) || !MAIN_TAB_PATHS.has(to.path)) return
    if (from.path === to.path) return

    console.log('[App Page Tracking] 主 Tab 切换', from.path, '→', to.path)
    // 1) 先上报旧 Tab stay（trackPageStay 内部用闭包 currentPageUrl 解析旧 trackName）
    trackPageStay()
    // 2) 更新基线，让下次 stay 归属新 Tab
    currentPageUrl = window.location.href
    pageEnterTime = Date.now()
    visibilityState.accumulatedTime = 0
    visibilityState.lastVisibleTime = Date.now()
    // 3) 上报新 Tab view（trackPageView 内部从 router.currentRoute 取最新 meta.track_name）
    trackPageView()
  })

  console.log('[App Page Tracking] 初始化完成')
}

/**
 * 手动上报页面曝光
 *
 * 使用场景：
 * - 页面初始加载
 * - 从后台切换到前台
 * - 特殊业务场景需要重新上报曝光
 */
export function trackPageView() {
  const route = router.currentRoute.value

  // router-ready 守卫：vue-router 的 START_LOCATION_NORMALIZED 占位状态下
  // path='/'、name=undefined、meta={}，此时上报会拼出裸 `_page_view`。
  // tcAppOnAppear（Native 推送）可能早于 router.isReady() 完成（首次冷启动 / Mine 等独立 WebView 入口）。
  // 通过 isReady 等初始导航完成后再补发一次，保证首屏 view 上报正确。
  if (!route.name) {
    console.log('[App Page Tracking] router 未就绪，等待 isReady 后补发 view')
    router.isReady().then(() => {
      // isReady 完成后路由已稳定，递归一次走正常分支
      if (router.currentRoute.value.name) {
        trackPageView()
      }
    })
    return
  }

  const trackName = (route.meta?.track_name as string) || route.path

  // trackName 为空（route 配置缺 meta.track_name）兜底
  if (!trackName) {
    console.warn('[App Page Tracking] trackName 为空，跳过 page_view, route=', route.name)
    return
  }

  // 生成事件名：{page}_page_view
  const eventName = `${trackName.replace(/^\/+|\/+$/g, '').replace(/\//g, '_').replace(/:/g, '').toLowerCase()}_page_view`

  track(eventName, {
    url: window.location.href,
    page_title: document.title,
    page_path: window.location.pathname,
    page_host: window.location.host,
    route_name: route.name as string,
  })

  console.log('[App Page Tracking] 页面曝光:', eventName, 'route:', route.name)
}

/**
 * 手动上报页面停留
 *
 * 使用场景：
 * - App 切换到后台（tcAppOnPause）- 主要触发时机
 * - 页面卸载（beforeunload）- 兜底触发时机
 */
export function trackPageStay() {
  if (!currentPageUrl || pageEnterTime === 0) {
    return
  }

  // 计算停留时长（只计算可见时间）
  const duration = calculateStayDuration()

  // 过滤掉停留时间太短的（小于 50ms）
  if (duration < 50) {
    return
  }

  // trackName 必须从闭包 currentPageUrl（旧 URL）反查，**不能用 router.currentRoute**：
  // router.afterEach 触发时 currentRoute 已 = to，会让 stay 错报新 Tab 名。
  // 此处实现与 event-tracker.ts:230-238 onBeforeTrack 处理 page_stay 的逻辑一致。
  let trackName = ''
  let routeName: string | undefined
  try {
    const url = new URL(currentPageUrl, window.location.origin)
    const matched = router.resolve(url.pathname)
    trackName = (matched.meta?.track_name as string) || ''
    routeName = matched.name as string | undefined
  } catch {
    // currentPageUrl 异常时降级 —— 仍不读 currentRoute，避免错报新 Tab 名
    trackName = ''
  }

  // 没拿到 trackName 不上报，避免裸 `_page_stay`
  if (!trackName) {
    console.warn('[App Page Tracking] trackName 为空，跳过 page_stay, currentPageUrl=', currentPageUrl)
    return
  }

  // 生成事件名：{page}_page_stay
  const eventName = `${trackName.replace(/^\/+|\/+$/g, '').replace(/\//g, '_').replace(/:/g, '').toLowerCase()}_page_stay`

  // 使用客户端代理上报，防止 webview 快速销毁导致埋点丢失
  track(eventName, {
    url: currentPageUrl,
    stay_duration: duration,
    page_title: document.title,
    route_name: routeName,
  }, { immediate: true })

  console.log('[App Page Tracking] 页面停留上报（客户端代理）:', eventName, duration, 'ms')

  // 重置状态：上报后重新开始计时
  pageEnterTime = Date.now()  // 重置进入时间
  visibilityState.accumulatedTime = 0  // 清空累计时间
  visibilityState.lastVisibleTime = Date.now()  // 重置为当前时间（而不是0）
}

/**
 * 计算实际停留时长（只计算可见时间）
 */
function calculateStayDuration(): number {
  let total = visibilityState.accumulatedTime

  // 如果当前页面可见，加上当前可见时长
  if (visibilityState.isVisible && visibilityState.lastVisibleTime > 0) {
    total += Date.now() - visibilityState.lastVisibleTime
  }

  return total
}
